import React, { useState, useRef, useEffect } from "react";
import { X, Plus, Save, Upload, Eye, Trash2, Link, Folder, CheckCircle2, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getFileIcon, getFileIconColor } from "../utils/fileIcons";
import { useProject } from "../contexts/ProjectContext";
import { useNotification } from "../contexts/NotificationContext";
import { projectAPI, categoryAPI } from "../utils/api";
import SimpleQuillEditor from "./SimpleQuillEditor";
import CustomDatePicker from "./CustomDatePicker";
import CustomStatusDropdown from "./CustomStatusDropdown";

const CreateProjectModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    clientName: "",
    projectStatus: "new",
    projectType: "",
    category: "",
    startDate: "",
    endDate: "",
    liveSiteUrl: "",
    demoSiteUrls: [""],
    markupUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);
  const { createProject } = useProject();
  const { showToast } = useNotification();
  const navigate = useNavigate();

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoryAPI.getCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("Project name is required", "error");
      return;
    }

    if (!formData.projectType) {
      showToast("Project type is required", "error");
      return;
    }

    if (!formData.startDate) {
      showToast("Start date is required", "error");
      return;
    }

    setLoading(true);
    try {
      // Create the project first using ProjectContext to ensure state updates
      const projectData = {
        ...formData,
        // Filter out empty demo site URLs before sending
        demoSiteUrls: formData.demoSiteUrls.filter(url => url.trim() !== ""),
        attachments: [], // Will be uploaded separately
      };
      console.log(
        "🔄 CreateProjectModal: Calling createProject with data:",
        projectData
      );
      const createdProject = await createProject(projectData);
      console.log(
        "✅ CreateProjectModal: Project created successfully:",
        createdProject
      );

      // If there are files to upload, upload them after project creation
      if (uploadedFiles.length > 0) {
        console.log("Uploading files:", uploadedFiles);
        console.log("Project ID for upload:", createdProject._id);

        if (!createdProject._id) {
          console.error("No project ID available for file upload");
          showToast(
            "Project created but file upload failed - no project ID",
            "warning"
          );
          return;
        }
        const uploadFormData = new FormData();
        uploadedFiles.forEach((fileObj, index) => {
          if (fileObj.file) {
            console.log(`Adding file ${index}:`, fileObj.name, fileObj.file);
            uploadFormData.append(`file${index}`, fileObj.file);
          }
        });

        try {
          console.log("Uploading files to project:", createdProject._id);
          const response = await projectAPI.uploadFiles(
            createdProject._id,
            uploadFormData
          );

          console.log("File upload successful:", response.data);

          // Update the uploadedFiles state to show the uploaded files
          const uploadedFileObjects = uploadedFiles.map((fileObj, index) => ({
            id: `uploaded-${Date.now()}-${index}`,
            name: fileObj.name,
            size: fileObj.size,
            type: fileObj.type,
            url: response.data.attachments[index]?.url || fileObj.url,
            uploaded: true,
          }));

          setUploadedFiles(uploadedFileObjects);

          showToast(
            `${uploadedFiles.length} file(s) uploaded successfully!`,
            "success"
          );
        } catch (uploadError) {
          console.error("File upload error:", uploadError);
          showToast(
            "Project created but some files failed to upload",
            "warning"
          );
        }
      }

      showToast(
        `Project created successfully${uploadedFiles.length > 0
          ? ` with ${uploadedFiles.length} file(s)`
          : ""
        }!`,
        "success"
      );

      console.log(
        "✅ CreateProjectModal: Project creation completed, showing success modal"
      );

      setCreatedProjectId(createdProject._id);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("Create project error:", error);
      showToast(error?.response?.data?.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      // If projectType changes and is not "ongoing", clear endDate
      if (name === "projectType" && value !== "ongoing") {
        return {
          ...prev,
          [name]: value,
          endDate: "",
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  // Handlers for demo site URLs array
  const addDemoSiteUrl = () => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: [...prev.demoSiteUrls, ""],
    }));
  };

  const removeDemoSiteUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: prev.demoSiteUrls.filter((_, i) => i !== index),
    }));
  };

  const handleDemoSiteUrlChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      demoSiteUrls: prev.demoSiteUrls.map((url, i) =>
        i === index ? value : url
      ),
    }));
  };


  // File upload handlers
  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((file) => {
      const maxSize = 25 * 1024 * 1024; // 25MB
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
        "application/json",
      ];

      if (file.size > maxSize) {
        showToast(
          `File ${file.name} is too large. Maximum size is 25MB.`,
          "error"
        );
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        showToast(`File type ${file.type} is not supported.`, "error");
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Create temporary file objects for preview
    const newFiles = validFiles.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file), // Temporary URL for preview
      file: file, // Store actual file for upload
      uploading: false,
      uploaded: false,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    showToast(`${validFiles.length} file(s) added successfully`, "success");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    handleFileUpload(files);
    e.target.value = ""; // Reset input
  };

  const removeFile = (fileId) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const openFilePreview = (file) => {
    window.open(file.url, "_blank");
  };

  if (showSuccessModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-md w-full p-8 text-center animate-fade-in transition-colors duration-200">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Project Created!</h2>
          <p className="text-gray-600 dark:text-slate-400 mb-8">
            Your project "{formData.name}" has been successfully created. What would you like to do next?
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                if (onSuccess) onSuccess();
                else onClose();
              }}
              className="w-full py-3 px-6 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-all duration-200"
            >
              Go to Existing Projects
            </button>
            <button
              onClick={() => {
                if (onSuccess) onSuccess();
                else onClose();
                navigate(`/project/${createdProjectId}`);
              }}
              className="w-full py-3 px-6 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg"
            >
              Open Newly Created Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden relative flex flex-col transition-colors duration-200"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 h-[61.19px] min-h-[61.19px] max-h-[61.19px] bg-[#f1f2f4] dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 shrink-0 box-border rounded-t-lg transition-colors duration-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 min-w-[32px] min-h-[32px] bg-primary-500 rounded-lg flex items-center justify-center text-white">
              <Plus className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-base font-semibold dark:text-white leading-tight">Create New Project</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 leading-tight">
                Set up a comprehensive project with all necessary details
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 min-w-[32px] min-h-[32px] max-w-[32px] max-h-[32px] flex items-center justify-center rounded-lg hover:bg-gray-400/20 transition-colors text-gray-600 dark:text-slate-400"
            title="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">
            <form
              id="create-project-form"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Section 1: Project Information */}
              <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center mr-3">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Project Information
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* First Row: Project Name, Client Name, Project Status */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Project Name */}
                    <div className="md:col-span-1">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        Project Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                        placeholder="Enter project name"
                        required
                      />
                    </div>
                    {/* Client Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        Client Name
                      </label>
                      <input
                        type="text"
                        name="clientName"
                        value={formData.clientName}
                        onChange={handleChange}
                        className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                        placeholder="Enter client name"
                      />
                    </div>
                    {/* Project Status */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        Project Status <span className="text-red-500">*</span>
                      </label>
                      <CustomStatusDropdown
                        value={formData.projectStatus}
                        onChange={(value) => setFormData((prev) => ({ ...prev, projectStatus: value }))}
                        options={[
                          { value: "new", label: "New" },
                          { value: "ongoing", label: "Ongoing" },
                          { value: "completed", label: "Completed" },
                          { value: "cancelled", label: "Cancelled" },
                        ]}
                      />
                    </div>
                  </div>

                  {/* Second Row: Project Type, Start Date, End Date */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Project Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        Project Type <span className="text-red-500">*</span>
                      </label>
                      <CustomStatusDropdown
                        value={formData.projectType}
                        onChange={(value) => {
                          setFormData((prev) => ({
                            ...prev,
                            projectType: value,
                            // Clear endDate if projectType is not "ongoing"
                            endDate: value !== "ongoing" ? "" : prev.endDate,
                          }));
                        }}
                        options={[
                          { value: "maintenance", label: "Maintenance" },
                          { value: "ongoing", label: "Ongoing" },
                          { value: "one-time", label: "One Time" },
                        ]}
                      />
                    </div>
                    {/* Start Date */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        Start Date <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowStartDatePicker(!showStartDatePicker)}
                        className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 flex items-center justify-between hover:border-gray-400 dark:hover:border-slate-500"
                      >
                        <span className={formData.startDate ? "text-black dark:text-white" : "text-gray-400"}>
                          {formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "Select start date"}
                        </span>
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </button>
                      {showStartDatePicker && (
                        <div ref={startDatePickerRef} className="relative">
                          <CustomDatePicker
                            value={formData.startDate}
                            onChange={(date) => {
                              setFormData((prev) => ({
                                ...prev,
                                startDate: date,
                              }));
                            }}
                            onClose={() => setShowStartDatePicker(false)}
                          />
                        </div>
                      )}
                    </div>
                    {/* End Date */}
                    <div className="relative">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                        End Date
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowEndDatePicker(!showEndDatePicker)}
                        className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 flex items-center justify-between hover:border-gray-400 dark:hover:border-slate-500"
                      >
                        <span className={formData.endDate ? "text-black dark:text-white" : "text-gray-400"}>
                          {formData.endDate ? new Date(formData.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "Select end date"}
                        </span>
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </button>
                      {showEndDatePicker && (
                        <div ref={endDatePickerRef} className="relative">
                          <CustomDatePicker
                            value={formData.endDate}
                            onChange={(date) => {
                              setFormData((prev) => ({
                                ...prev,
                                endDate: date,
                              }));
                            }}
                            onClose={() => setShowEndDatePicker(false)}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Category
                    </label>
                    <CustomStatusDropdown
                      value={formData.category}
                      onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
                      options={[
                        { value: "", label: "No Category" },
                        ...categories.map((cat) => ({
                          value: cat._id,
                          label: cat.name,
                        })),
                      ]}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Description
                    </label>
                    <div className="border border-gray-300 dark:border-slate-600 rounded-lg overflow-hidden">
                      <SimpleQuillEditor
                        value={formData.description}
                        onChange={(content) =>
                          setFormData((prev) => ({
                            ...prev,
                            description: content,
                          }))
                        }
                        placeholder="Enter project description"
                        height="200px"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Site URLs */}
              <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                    <Link className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Site URLs
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Live Site URL */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Live Site URL
                    </label>
                    <input
                      type="url"
                      name="liveSiteUrl"
                      value={formData.liveSiteUrl}
                      onChange={handleChange}
                      className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                      placeholder="https://example.com"
                    />
                  </div>
                  {/* Demo Site URL */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300">
                        Demo Site URL
                      </label>
                    </div>
                    <div className="space-y-2">
                      {formData.demoSiteUrls.map((url, index) => (
                        <div key={index} className="flex items-center gap-1.5">
                          <div className="relative flex-1 flex items-center">
                            <input
                              type="url"
                              value={url}
                              onChange={(e) =>
                                handleDemoSiteUrlChange(index, e.target.value)
                              }
                              className={`w-full h-8 pl-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200 ${
                                index === 0 ? "pr-8" : "pr-3"
                              }`}
                              placeholder="https://demo.example.com"
                            />
                            {index === 0 && (
                              <button
                                type="button"
                                onClick={addDemoSiteUrl}
                                className="absolute right-1 w-6 h-6 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-md transition-all duration-200 hover:scale-105"
                                title="Add demo site URL"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {formData.demoSiteUrls.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeDemoSiteUrl(index)}
                              className="w-8 h-8 min-w-[32px] min-h-[32px] flex items-center justify-center text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                              title="Remove this URL"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Markup URL */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Markup URL
                    </label>
                    <input
                      type="url"
                      name="markupUrl"
                      value={formData.markupUrl}
                      onChange={handleChange}
                      className="w-full h-8 px-3 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-black dark:text-white rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-xs transition-all duration-200"
                      placeholder="https://markup.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Files and Documents */}
              <div className="bg-gray-50 dark:bg-slate-700/30 rounded-lg p-4 border border-gray-200 dark:border-slate-700 transition-colors duration-200">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <Upload className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Files and Documents
                  </h3>
                </div>

                {/* Drag and Drop Area */}
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${isDragOver
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500"
                    }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600 dark:text-slate-400 mb-2">
                    Drag and drop files here, or{" "}
                    <button
                      type="button"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      browse files
                    </button>
                  </p>
                  <p className="text-sm text-gray-500 dark:text-slate-500">
                    Supports images, PDFs, documents (max 25MB each)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileInputChange}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.json"
                  />
                </div>

                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                      Uploaded Files ({uploadedFiles.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${file.uploaded
                            ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                            : "bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600"
                            }`}
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 bg-gray-100 dark:bg-slate-800 rounded flex items-center justify-center">
                                {React.createElement(
                                  getFileIcon(file.type, file.name),
                                  {
                                    className: `w-5 h-5 ${getFileIconColor(
                                      file.type,
                                      file.name
                                    )}`,
                                  }
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-slate-400">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {file.uploaded && (
                              <div
                                className="p-1 text-green-600"
                                title="Successfully uploaded"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => openFilePreview(file)}
                              className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                              title="View file"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFile(file.id)}
                              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                              title="Remove file"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 px-6 py-3 transition-colors duration-200">
          <div className="flex justify-end items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="h-8 px-4 text-gray-600 dark:text-slate-300 hover:text-gray-800 dark:hover:text-white font-medium text-xs transition-all duration-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-300 dark:border-slate-600 flex items-center justify-center"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-project-form"
              className="h-8 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 font-medium text-xs px-4 rounded-lg transition-all duration-200 flex items-center space-x-1.5 disabled:opacity-50 disabled:cursor-not-allowed shadow hover:shadow-md"
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Create Project</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectModal;
